import { Table, Text } from '@mantine/core';
import React from 'react';
import { formatMoney } from 'src/common/currency';
import { formatDate } from 'src/financial/financial-data/tables/FinancialItemGenericTable';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { FinancialRow } from './financialRows';
import { AssetFeedRowIcon } from 'src/financial/financial-data/AssetFeedIndicators';
import { useCurrency } from 'src/common/hooks/useCurrency';

// Named owners win where the view spans the family, since no single member is "me" there and an
// item can belong to several. Otherwise: undefined → name view; a number → "<n>%"; null → joint
// with no share recorded yet.
const ownershipDisplay = (row: FinancialRow) => {
  if (row.ownerNames && row.ownerNames.length > 0) return row.ownerNames.join(', ');
  if (row.ownershipPercentage === undefined) return row.memberName;
  if (row.ownershipPercentage === null) return '—';
  return `${row.ownershipPercentage}%`;
};

export function FinancialRows({
  rows,
  onRowClick,
  showOwnership = false,
  ownershipLabel = 'Ownership',
}: {
  rows: FinancialRow[];
  onRowClick?: (row: FinancialRow) => void;
  showOwnership?: boolean;
  ownershipLabel?: string;
}) {
  const currency = useCurrency();
  const columns = [
    ...(showOwnership ? [ownershipLabel] : []),
    'Description',
    'Provider',
    'Type',
    'Valuation date',
    'Valuation',
  ];

  return (
    <Table
      withRowBorders={false}
      verticalSpacing={0}
      horizontalSpacing={0}
      style={{ borderCollapse: 'separate', borderSpacing: '0 4px' }}
      styles={{ thead: { backgroundColor: 'transparent' } }}
    >
      <Table.Thead>
        <Table.Tr>
          {columns.map((column, index) => (
            <Table.Th
              key={column}
              style={{ padding: 8, textAlign: index === columns.length - 1 ? 'right' : 'left' }}
            >
              <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.5">
                {column}
              </Text>
            </Table.Th>
          ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map(row => (
          <Table.Tr
            key={row.id}
            style={{ cursor: onRowClick ? 'pointer' : undefined }}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            {showOwnership && <RowCell>{ownershipDisplay(row)}</RowCell>}
            <RowCell>
              {row.description || '—'}
              <AssetFeedRowIcon
                isUpdatedAutomatically={row.isUpdatedAutomatically}
                hasFeedData={row.hasFeedData}
              />
            </RowCell>
            <RowCell>{row.provider}</RowCell>
            <RowCell>{row.typeLabel}</RowCell>
            <RowCell>{row.balanceDate ? formatDate(row.balanceDate) : undefined}</RowCell>
            <RowCell align="right">
              {row.balance != null ? formatMoney(row.balance, currency) : undefined}
            </RowCell>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function RowCell({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <Table.Td style={{ padding: 8, textAlign: align }}>
      <Text size="xs" fw={FONT_WEIGHTS.medium} c="black">
        {children || '—'}
      </Text>
    </Table.Td>
  );
}
